
import { useState, useRef, useEffect } from 'react';

interface VideoPreviewProps {
  videoUrl: string;
  thumbnail: string;
  title: string;
  isLive?: boolean;
  isReplay?: boolean;
  onClose: () => void;
}

export default function VideoPreview({ videoUrl, thumbnail, title, isLive = false, isReplay = false, onClose }: VideoPreviewProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoadStart = () => setIsLoading(true);
      const handleCanPlay = () => {
        setIsLoading(false);
        // 回放视频加载完成后自动播放
        if (isReplay) {
          video.play().catch(() => {
            // 自动播放失败时静默处理
          });
        }
      };
      const handleError = () => {
        setIsLoading(false);
        setHasError(true);
      };

      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', handleError);

      return () => {
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
      };
    }
  }, [isReplay]);

  // 直播画面刷新功能
  useEffect(() => {
    if (isLive) {
      // 每30秒刷新一次直播画面
      refreshIntervalRef.current = setInterval(() => {
        const video = videoRef.current;
        if (video && !video.paused) {
          const currentTime = video.currentTime;
          const wasPlaying = !video.paused;
          
          // 重新加载视频源
          video.load();
          
          // 恢复播放状态
          if (wasPlaying) {
            video.play().catch(() => {
              // 如果自动播放失败，静默处理
            });
          }
          
          setRefreshCount(prev => prev + 1);
        }
      }, 30000); // 30秒

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    }
  }, [isLive]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, []);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleManualRefresh = () => {
    const video = videoRef.current;
    if (video && isLive) {
      const wasPlaying = !video.paused;
      setIsLoading(true);
      
      // 手动刷新视频
      video.load();
      
      if (wasPlaying) {
        video.play().catch(() => {
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
      
      setRefreshCount(prev => prev + 1);
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center space-x-3">
            {isLive && (
              <div className="bg-red-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center">
                <div className="w-1 h-1 bg-white rounded-full mr-1 animate-pulse"></div>
                直播中
              </div>
            )}
            {isReplay && (
              <div className="bg-green-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center">
                <i className="ri-play-circle-line mr-1"></i>
                回放
              </div>
            )}
            <h3 className="font-semibold text-gray-900 text-sm md:text-base line-clamp-1">{title}</h3>
            {isLive && refreshCount > 0 && (
              <span className="text-xs text-gray-500">
                已刷新 {refreshCount} 次
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {isLive && (
              <button
                onClick={handleManualRefresh}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                title="手动刷新画面"
              >
                <i className="ri-refresh-line text-lg text-gray-600"></i>
              </button>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-lg text-gray-600"></i>
            </button>
          </div>
        </div>

        {/* 视频区域 */}
        <div className="relative bg-black">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
              <div className="text-center text-white">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                <p className="text-sm">
                  {refreshCount > 0 ? '刷新画面中...' : '加载中...'}
                </p>
              </div>
            </div>
          )}

          {hasError ? (
            <div className="aspect-video flex items-center justify-center bg-gray-900 text-white">
              <div className="text-center">
                <i className="ri-error-warning-line text-4xl mb-2 text-red-400"></i>
                <p className="text-sm">视频加载失败</p>
                <p className="text-xs text-gray-400 mt-1">请检查网络连接或稍后重试</p>
                {isLive && (
                  <button
                    onClick={handleManualRefresh}
                    className="mt-3 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors cursor-pointer text-sm"
                  >
                    重新连接直播
                  </button>
                )}
              </div>
            </div>
          ) : (
            <video
              ref={videoRef}
              className="w-full aspect-video"
              controls
              autoPlay={isLive || isReplay}
              muted={isLive}
              poster={thumbnail}
              preload="metadata"
              key={refreshCount} // 强制重新渲染
            >
              <source src={videoUrl} type="video/mp4" />
              <source src={videoUrl} type="video/webm" />
              <source src={videoUrl} type="application/x-mpegURL" />
              您的浏览器不支持视频播放
            </video>
          )}

          {/* 直播刷新提示 */}
          {isLive && (
            <div className="absolute top-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-xs flex items-center">
              <div className="w-1 h-1 bg-green-400 rounded-full mr-1 animate-pulse"></div>
              自动刷新
            </div>
          )}
        </div>

        {/* 底部控制区域 */}
        <div className="p-4 bg-gray-50 border-t">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button className="flex items-center space-x-1 text-gray-600 hover:text-red-500 cursor-pointer">
                <i className="ri-heart-line text-lg"></i>
                <span className="text-sm">点赞</span>
              </button>
              <button className="flex items-center space-x-1 text-gray-600 hover:text-blue-500 cursor-pointer">
                <i className="ri-share-line text-lg"></i>
                <span className="text-sm">分享</span>
              </button>
              <button className="flex items-center space-x-1 text-gray-600 hover:text-green-500 cursor-pointer">
                <i className="ri-download-line text-lg"></i>
                <span className="text-sm">下载</span>
              </button>
            </div>
            
            <div className="flex items-center space-x-2">
              {isLive && (
                <div className="flex items-center text-xs text-gray-500 mr-2">
                  <i className="ri-time-line mr-1"></i>
                  <span>30秒自动刷新</span>
                </div>
              )}
              <button className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer text-sm">
                画质
              </button>
              <button className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer text-sm">
                倍速
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
